import { useEffect, useState } from "react";
import {
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  CircularProgress,
  Button,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  SkipNext as IconSkipNext,
  NavigateNext as IconNavigateNext,
  NavigateBefore as IconNavigateBefore,
  SkipPrevious as IconSkipPrevious,
} from "@material-ui/icons";

import {
  DetectionRow,
  DetectionDataStatus,
  DetectionHistogramState,
  DetectionAbacusState,
  DetectionFilterInput,
} from "../../redux/detections/detection-types";
import FlexCol from "../common/FlexCol";
import FlexRow from "../common/FlexRow";
import ResizableSplitPanel from "../common/ResizableSplitPanel";
import { HistogramTimeSlider } from "../common/charts/HistogramTimeSlider";
import DetectionTable from "./DetectionTable";
import DetectionAbacus from "./DetectionAbacus";

type Mode = "table" | "abacus";
/** [ start, end ] */
export type IntervalUpdate = number[];
/** Force the overview histogram brush interval to expand, contract, or to a set interval */
export type ZoomOverride =
  | "in"
  | "out"
  | "prev"
  | "next"
  | "beginning"
  | "end"
  | "manual"      
  | IntervalUpdate
  | null;

export type DataBounds = {
  /** numeric timestamp with milliseconds */
  dtFirst: number;
  dtLast: number;
};

/** having this defined within the function prevented the brush interval from working properly */
const VEGA_ACTIONS = {
  export: { png: true, svg: false },
  compiled: false,
  editor: false,
  source: false,
};

const HISTOGRAM_AXIS_OPTIONS = {
  yAxis: {
    title: "Detections",
    width: 50,
  },
  xAxis: {
    title: "",
  },
};

const useStyles = makeStyles(() => ({
  main: {
    backgroundColor: "white",
  },
  /**
   * The gutter reserves space for the scrollbar so the histogram's x axis aligns
   * with abacus when there are too many receivers and the abacus scrolls
   *
   * This isn't supported on Safari currently :-(
   */
  visArea: {
    flexGrow: 1,
    marginTop: 8,
    overflowY: "auto",
    scrollbarGutter: "stable",
  },
  histArea: {
    flexGrow: 1,
    overflowY: "hidden",
    scrollbarGutter: "stable",
  },
}));

function NavDivider() {
  return (
    <div
      style={{
        display: "flex",
        height: "100%",
        borderRight: "1px solid lightgray",
        marginLeft: "16px",
        marginRight: "16px",
      }}
    ></div>
  );
}
export type TimeUnitType = "yearmonthdate" | "yearmonthdatehours" | "yearmonthdatehoursminutes";
export function DetectionVisualizer({
  detectionState,
  detectionRows,
  detectionCount,
  displayMode,
  setDisplayMode,
  histogram,
  abacus,
  abacusEnabled,
  tableLimitReached,
  overviewBinSeconds,
  abacusBinSeconds,
  setAbacusBinSeconds,
  currentFilters,
}: {
  detectionState: DetectionDataStatus;
  detectionRows: DetectionRow[];
  detectionCount?: number;
  displayMode: Mode;
  setDisplayMode: (mode: Mode) => void;
  histogram: DetectionHistogramState;
  abacus: DetectionAbacusState;
  abacusEnabled: boolean;
  tableLimitReached: boolean;
  overviewBinSeconds: number;
  abacusBinSeconds: number;
  setAbacusBinSeconds: (number) => void;
  currentFilters: DetectionFilterInput;
}) {
  const classes = useStyles();
  const [interval, setInterval] = useState([] as IntervalUpdate); // for HistogramTimeSlider to set selected interval for filtering results
  const [zoomOverride, setZoomOverride] = useState(null as ZoomOverride);
  const [searchText, setSearchText] = useState<string>("");

  function getTimeUnit(binSeconds: number): { unit: TimeUnitType; step: number } {
    return (
      binSeconds >= 86400
        ? { unit: "yearmonthdate" as TimeUnitType, step: binSeconds / 86400 }
        : binSeconds >= 3600
        ? { unit: "yearmonthdatehours" as TimeUnitType, step: binSeconds / 3600 }
        : { unit: "yearmonthdatehoursminutes" as TimeUnitType, step: binSeconds / 60 }
    ) as {
      unit: TimeUnitType;
      step: number;
    };
  }
  displayMode = abacusEnabled ? displayMode : "table";
  const [abacusTimeUnit, setAbacusTimeUnit] = useState(getTimeUnit(abacusBinSeconds));
  const overviewTimeUnit = getTimeUnit(overviewBinSeconds);
  const histogramReady = histogram.status === "COMPLETE";
  const histogramSelection =
    histogram.status === "COMPLETE" &&
    !(!interval || interval.length === 0 || interval[0] == interval[1]);

  const histLength = histogram?.countPerInterval?.length;
  /** first timestamp, last timestamp (with an hour padding) */
  const dataBounds: DataBounds = {
    dtFirst: (histLength && histogram.countPerInterval[0].dt) || 0,
    dtLast: (histLength && histogram.countPerInterval[histLength - 1].dt) || 0,
  };

  const intervalExceedsData = dataBounds.dtLast <= (interval?.[1] || 0);
  const intervalPreceedsData = dataBounds.dtFirst >= (interval?.[0] || 0);
  const intervalAtBeginning = dataBounds.dtFirst === (interval?.[0] || 0);
  const intervalAtMax =
    interval?.length && interval[1] - interval[0] >= dataBounds.dtLast - dataBounds.dtFirst
      ? true
      : false;

  useEffect(() => {
    setInterval([]);
  }, [currentFilters]);

  useEffect(() => {
    if (abacus.status === "COMPLETE" && histogramReady) {
      setAbacusTimeUnit(
        abacusBinSeconds >= 86400
          ? { unit: "yearmonthdate" as TimeUnitType, step: abacusBinSeconds / 86400 }
          : abacusBinSeconds >= 3600
          ? { unit: "yearmonthdatehours" as TimeUnitType, step: abacusBinSeconds / 3600 }
          : { unit: "yearmonthdatehoursminutes" as TimeUnitType, step: abacusBinSeconds / 60 }
      );
    }
  }, [abacus.status, abacusBinSeconds, histogramReady]);

  const needSelectionMsg =
    "Please make a selection (click and drag) on the overview plot below first";

  return (
    <FlexCol fullWidth fullHeight paddingLevel={1} className={classes.main}>
      <FlexRow marginLevel={1}>
        {abacusEnabled && (
          <>
            <FlexCol>
              <Typography variant="caption">Visualize By</Typography>
              <RadioGroup
                row
                value={displayMode}
                onChange={e => setDisplayMode(e.target.value as Mode)}
              >
                <FormControlLabel value="table" control={<Radio size="small" />} label="Table" />
                <FormControlLabel value="abacus" control={<Radio size="small" />} label="Abacus" />
              </RadioGroup>
            </FlexCol>
            <NavDivider />
          </>
        )}
        <FlexCol>
          <Typography variant="caption">Selected Time Frame</Typography>
          <FlexRow
            itemSpacing={1}
            paddingLevel={0.5}
            vAlign="center"
            title={
              histogramReady
                ? "You can select an area of the overview histogram (click and drag) to specify a time frame for the visualization below it"
                : "Please wait for data to load"
            }
          >
            <Button
              variant="outlined"
              size="small"
              style={{ textTransform: "none" }}
              onClick={() => setZoomOverride("in")}
              disabled={!histogramReady}
            >
              Zoom In
            </Button>
            <Button
              variant="outlined"
              size="small"
              style={{ textTransform: "none" }}
              onClick={() => setZoomOverride("out")}
              disabled={!histogramSelection || intervalAtMax}
            >
              Zoom Out
            </Button>
            <span
              title={`${
                !histogramSelection
                  ? needSelectionMsg
                  : intervalAtBeginning
                  ? "Already at the beginning"
                  : "Move the selection window to the beginning of the data"
              }`}
            >
              <Button
                variant="outlined"
                size="small"
                style={{ textTransform: "none" }}
                onClick={() => setZoomOverride("beginning")}
                disabled={!histogramSelection || intervalAtBeginning}
              >
                <IconSkipPrevious />
              </Button>
            </span>
            <span
              title={`${
                !histogramSelection
                  ? needSelectionMsg
                  : intervalPreceedsData
                  ? "Cannot go previous since the selection extends before the beginning of the data"
                  : "Move the selection window to the previous adjacent time frame"
              }`}
            >
              <Button
                variant="outlined"
                size="small"
                style={{ textTransform: "none" }}
                onClick={() => setZoomOverride("prev")}
                disabled={!histogramSelection || intervalPreceedsData}
              >
                <IconNavigateBefore />
              </Button>
            </span>
            <span
              title={`${
                !histogramSelection
                  ? needSelectionMsg
                  : intervalExceedsData
                  ? "Cannot go next since the selection extends past the end of the data"
                  : "Move the selection window to the next adjacent time frame"
              }`}
            >
              <Button
                variant="outlined"
                size="small"
                style={{ textTransform: "none" }}
                onClick={() => setZoomOverride("next")}
                disabled={!histogramSelection || intervalExceedsData}
              >
                <IconNavigateNext />
              </Button>
            </span>
            <span
              title={`${
                !histogramSelection
                  ? needSelectionMsg
                  : intervalExceedsData
                  ? "Already at the end"
                  : "Move the selection window to the end of the data"
              }`}
            >
              <Button
                variant="outlined"
                size="small"
                style={{ textTransform: "none" }}
                onClick={() => setZoomOverride("end")}
                disabled={!histogramSelection || intervalExceedsData}
              >
                <IconSkipNext />
              </Button>
            </span>
          </FlexRow>
        </FlexCol>
      </FlexRow>
      <ResizableSplitPanel
        direction="vertical"
        firstInit="20%"
        firstMin={100}
        firstContent={
          <>
            <FlexCol className={classes.histArea}>
              {histogram.status === "UNLOADED" ? (
                <div></div>
              ) : ["INITIAL_LOADING"].includes(histogram.status) ? (
                <FlexCol fullWidth fullHeight vAlign="center" hAlign="center">
                  <CircularProgress />
                </FlexCol>
              ) : ["LOADING", "COMPLETE"].includes(histogram.status) ? (
                <HistogramTimeSlider
                  timeUnit={overviewTimeUnit}
                  loading={histogram.status === "LOADING"}
                  countPerInterval={histogram.countPerInterval}
                  onIntervalUpdate={setInterval}
                  axisOptions={HISTOGRAM_AXIS_OPTIONS}
                  actions={VEGA_ACTIONS}
                  zoomOverride={zoomOverride}
                  resetZoomOverride={() => setZoomOverride(null)}
                  setZoomOverrideManual={() => setZoomOverride("manual")}
                  binSeconds={overviewBinSeconds}
                />
              ) : (
                <div>An unexpected error occured</div>
              )}
            </FlexCol>
          </>
        }
        secondContent={
          <FlexCol className={classes.visArea}>
            {displayMode === "table" && (
              <DetectionTable
                tableLimitReached={tableLimitReached}
                detectionState={detectionState}
                detectionRows={detectionRows}
                searchText={searchText}
                setSearchText={setSearchText}
                interval={interval}
                detectionCount={detectionCount}
              />
            )}
            {displayMode === "abacus" &&
              (abacus.status === "UNLOADED" ? (
                <div></div>
              ) : ["INITIAL_LOADING"].includes(abacus.status) ? (
                <FlexCol fullWidth fullHeight hAlign="center" paddingLevel={2}>
                  <CircularProgress />
                </FlexCol>
              ) : ["LOADING", "COMPLETE"].includes(abacus.status) ? (
                <DetectionAbacus
                  timeUnit={abacusTimeUnit}
                  loading={abacus.status === "LOADING"}
                  interval={interval}
                  byRxData={abacus.byRx}
                  actions={VEGA_ACTIONS}
                  setZoomWindow={setZoomOverride}
                  setAbacusBinSeconds={setAbacusBinSeconds}
                  currentFilters={currentFilters}
                  histogramReady={histogramReady}
                  dataBounds={dataBounds}
                />
              ) : (
                <div>An unexpected error occured</div>
              ))}
          </FlexCol>
        }
      />
    </FlexCol>
  );
}
